import * as React from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { cn } from '../../cn';
import { useLanguage } from '../../providers/LanguageContext';

type DialogRootProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root> & {
  deepLinkId?: string;
};

export const Dialog: React.FC<DialogRootProps> = ({ deepLinkId, open: controlledOpen, defaultOpen, onOpenChange, children, ...rest }) => {
  const isControlled = typeof controlledOpen === 'boolean';
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState<boolean>(Boolean(defaultOpen));
  const open = isControlled ? (controlledOpen as boolean) : uncontrolledOpen;

  // Initialize from URL deep-link if provided
  React.useEffect(() => {
    if (!deepLinkId) return;
    try {
      const url = new URL(window.location.href);
      const shouldOpen = url.searchParams.get('open') === deepLinkId;
      if (shouldOpen) {
        if (isControlled) onOpenChange?.(true);
        else setUncontrolledOpen(true);
      }
    } catch { /* ignore for non-browser */ }
    // only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkId]);

  const handleOpenChange = (next: boolean) => {
    // Reflect in URL if deepLinkId provided
    if (deepLinkId) {
      try {
        const url = new URL(window.location.href);
        const current = url.searchParams.get('open');
        if (next) {
          if (current !== deepLinkId) {
            url.searchParams.set('open', deepLinkId);
            window.history.replaceState(null, '', url.toString());
          }
        } else if (current === deepLinkId) {
          url.searchParams.delete('open');
          window.history.replaceState(null, '', url.toString());
        }
      } catch { /* ignore */ }
    }

    if (isControlled) onOpenChange?.(next);
    else setUncontrolledOpen(next);
  };

  return (
    <DialogPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={handleOpenChange} {...rest}>
      {children}
    </DialogPrimitive.Root>
  );
};
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;

export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>>(
  function DialogOverlay(props, ref) {
    return (
      <DialogPrimitive.Overlay
        ref={ref}
        className="fixed inset-0 z-40 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 bg-overlay"
        {...props}
      />
    );
  }
);

export type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /** When provided, a dropdown slot is rendered (sibling of Content) for Select/Popover to portal into; avoids DismissableLayer pointer-events conflict. */
  dropdownSlotRef?: React.RefObject<HTMLDivElement | null>;
};

export const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  function DialogContent({ className = '', children, dropdownSlotRef, onPointerDownOutside, style: styleProp, ...props }, ref) {
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const triggerRef = React.useRef<HTMLElement | null>(null);
    const { t } = useLanguage();
    // Optional: when present, we reflect dialog open state into URL (?open=<id>) for deep-linking
    // Consumers can pass data-dialog-id on DialogContent to control the value

    // Lock body scroll when dialog is open (DialogContent is only rendered when open)
    React.useEffect(() => {
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      
      // Prevent layout shift from scrollbar disappearing
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }

      // Cleanup: restore body scroll on unmount/close
      return () => {
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
      };
    }, []);

    React.useEffect(() => {
      // Save the active element (likely the trigger) to restore on close
      triggerRef.current = (document.activeElement as HTMLElement) || null;
      const el = contentRef.current;
      if (!el) return;

      // NOTE (BUG-2026-06-16-WGC1P): a11y title/description are now guaranteed
      // structurally via the visually-hidden Radix fallback rendered in JSX below
      // (see the `VisuallyHidden.Root` block inside DialogPrimitive.Content). The
      // previous imperative DOM injection of an `<h2 data-dialog-title>` did NOT
      // satisfy Radix — Radix tracks the registered `DialogPrimitive.Title`
      // component through React context, not a DOM scan — so it never suppressed
      // the console.error. That fake gate has been removed in favour of a real one.

      // Focus first autofocus or focusable element if none focused
      const autofocusTarget = el.querySelector('[autofocus]') as HTMLElement | null;
      const firstFocusable = el.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const current = document.activeElement as HTMLElement | null;
      if (!current || !el.contains(current)) {
        (autofocusTarget || firstFocusable || el).focus();
      }
      return () => {
        // On unmount/close, restore focus to the trigger if still in the document
        const t = triggerRef.current;
        if (t && document.contains(t)) {
          try { t.focus(); } catch (e) { /* ignore */ }
        }
      };
    }, [t]);

    // Note: open/close URL reflection is handled by Dialog Root when deepLinkId is provided

    return (
      <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogPrimitive.Content
          data-component="Dialog"
          data-package="@appic/ui-components"
          ref={(node) => {
            if (typeof ref === 'function') ref(node);
            else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            contentRef.current = node;
          }}
          role="dialog"
          aria-modal="true"
          className={cn("fixed z-51 flex flex-col w-dialog max-w-md max-h-screen-inset duration-300 focus:outline-none data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-top-4 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-top-4 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-card text-card-foreground overflow-hidden opacity-100 shadow-card", className)}
          style={styleProp}
          data-voice-ready
          data-voice-role="dialog"
          onPointerDownOutside={(e) => {
            // Keep dialog open when user interacts with portaled Select/Popover (body-level dropdown) or Radix popper content
            const target = e.target as Element;
            if (target.closest?.('[data-dropdown-portal]') || target.closest?.('[data-radix-popper-content-wrapper]')) {
              e.preventDefault();
            }
            onPointerDownOutside?.(e);
          }}
          {...props}
        >
          {/* Single scroll container: flex-1 min-h-0 so this flex child can shrink and scroll. */}
          <div className="flex-1 min-h-0 overflow-y-auto p-6 overscroll-contain">
            {children}
          </div>
          {/* CLASS-GATE (BUG-2026-06-16-WGC1P): always-present sr-only Radix Title +
              Description so Radix's required-a11y check is satisfied structurally —
              a missing title can no longer ship from any call site or conditional
              branch. Rendered AFTER `children` on purpose: Radix derives the dialog's
              accessible name from the FIRST element carrying the shared title id
              (document order), so a consumer's own `DialogTitle` inside `children`
              still wins the accessible name; this fallback only takes over when no
              consumer title exists. Either way the
              "`DialogContent` requires a `DialogTitle`" console.error is impossible.
              This replaces the previous imperative DOM injection, which Radix ignored
              because it tracks the registered Title component, not a DOM scan. */}
          <DialogPrimitive.Title className="sr-only">{t('common.dialog.defaultTitle')}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only" />
        </DialogPrimitive.Content>
        {dropdownSlotRef && (
          <div
            ref={dropdownSlotRef as React.RefObject<HTMLDivElement>}
            id="dialog-dropdown-slot"
            aria-hidden
            className="fixed z-100"
          />
        )}
      </DialogPrimitive.Portal>
    );
  }
);

export const DialogTitle = React.forwardRef<HTMLHeadingElement, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>>(
  function DialogTitle({ className = '', ...props }, ref) {
    return (
      <DialogPrimitive.Title ref={ref} className={cn("text-lg font-semibold text-foreground", className)} {...props} />
    );
  }
);

export const DialogDescription = React.forwardRef<HTMLParagraphElement, React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>>(
  function DialogDescription({ className = '', ...props }, ref) {
    return (
      <DialogPrimitive.Description ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
    );
  }
);
export const DialogHeader = ({ className = '', ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);

export const DialogFooter = ({ className = '', ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse gap-3 sm:flex-row sm:justify-end", className)} {...props} />
);
